import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ShareActions from "@/components/ShareActions";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { BookOpenText, ArrowUpRight, Filter, ArrowLeft } from "lucide-react";
import { sanitizeResourceHtml } from "@/lib/sanitizeResourceHtml";

const DEFAULT_SECTORS = [
  "All",
  "Beauty & Cosmetics",
  "Skincare",
  "Lip Care",
  "Apparel & Textile",
  "Food & Beverage",
  "FMCG",
  "Home & Lifestyle",
  "Consumer Electronics",
  "Nutraceuticals",
];

export default function Resources() {
  const { resourceId } = useParams();
  const [resources, setResources] = useState([]);
  const [selectedSector, setSelectedSector] = useState("All");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (resourceId) {
          const { data } = await axios.get(`${API}/resources/${resourceId}`);
          setDetail(data || null);
          setResources([]);
        } else {
          const { data } = await axios.get(`${API}/resources`);
          setResources(data || []);
          setDetail(null);
        }
      } catch {
        setResources([]);
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [resourceId]);

  const sectors = Array.from(new Set(["All", ...resources.flatMap((item) => item.industry_sector || [])])).filter(Boolean);
  const visibleResources = selectedSector === "All"
    ? resources
    : resources.filter((item) => (item.industry_sector || []).includes(selectedSector));

  if (resourceId && detail) {
    return (
      <div className="min-h-screen bg-bone">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
          <Link to="/resources" className="inline-flex items-center gap-2 text-sm text-klein mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to resources
          </Link>
          <div className="editorial-card p-8">
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="tag text-[10px] uppercase">{detail.resource_type || "article"}</span>
              {(detail.industry_sector || []).slice(0, 3).map((sector) => <span key={sector} className="tag text-[10px]">{sector}</span>)}
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl leading-tight mb-4">{detail.title}</h1>
            <p className="text-sm text-[--muted-foreground] mb-6">{detail.short_description}</p>
            <div className="border-y border-[--border-soft] py-4 mb-8">
              <div className="overline text-[10px] mb-3">SHARE THIS RESOURCE</div>
              <ShareActions
                url={window.location.href}
                title={detail.title}
                text={detail.short_description || "Explore this AskWinn resource."}
              />
            </div>
            {detail.content ? (
              <div className="resource-content prose prose-sm max-w-none whitespace-pre-wrap text-[--ink]" dangerouslySetInnerHTML={{ __html: sanitizeResourceHtml(detail.content) }} />
            ) : (
              <div className="prose prose-sm max-w-none text-[--ink]">No content available.</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="overline mb-4 text-burn">§ KNOWLEDGE HUB</div>
        <h1 className="font-serif text-5xl lg:text-6xl font-light leading-none tracking-tight mb-4">
          Public <em className="text-klein not-italic">resources</em>.
        </h1>
        <p className="max-w-2xl text-[--muted-foreground] mb-10">
          Explore product briefs, sector guides, and sourcing insights built for buyers and manufacturers.
        </p>

        <div className="editorial-card p-4 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-klein" />
            <div className="overline text-[10px]">FILTER BY SECTOR</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {(sectors.length ? sectors : DEFAULT_SECTORS).map((sector) => (
              <button
                key={sector}
                type="button"
                onClick={() => setSelectedSector(sector)}
                className={`tag cursor-pointer ${selectedSector === sector ? "verified" : ""}`}
              >
                {sector}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="editorial-card p-10 text-center font-mono text-sm">Loading resources…</div>
        ) : visibleResources.length === 0 ? (
          <div className="editorial-card p-10 text-center font-mono text-sm">— No public resources available yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleResources.map((resource) => (
              <article key={resource.resource_id} className="editorial-card p-5 flex flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="tag text-[10px] uppercase">{resource.resource_type || "article"}</span>
                  {resource.industry_sector?.[0] && <span className="font-mono text-[10px] text-[--muted-foreground]">{resource.industry_sector[0]}</span>}
                </div>
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-klein/10 p-2 text-klein">
                    <BookOpenText className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="font-serif text-2xl leading-tight">{resource.title}</h2>
                  </div>
                </div>
                <p className="text-sm text-[--muted-foreground]">{resource.short_description || resource.content?.slice(0, 140) || "View this resource for more details."}</p>
                <div className="flex flex-wrap gap-2">
                  {(resource.tags || []).slice(0, 4).map((tag) => (
                    <span key={tag} className="tag text-[10px]">{tag}</span>
                  ))}
                </div>
                <Link to={`/resources/${resource.resource_id}`} className="mt-auto inline-flex items-center gap-2 text-sm text-klein font-medium">
                  Read resource <ArrowUpRight className="w-4 h-4" />
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
